// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullChampionship,
  $FullChampionshipResultTable,
  $FullChampionshipResultTableEntry,
  $FullChampionshipResultTableRace,
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getChampionshipEntrySubTitle,
  getChampionshipEntryTitle,
  sortResults,
} from 'common.helpers/dist/browser';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import NoData from '../../../common/NoData';

// Types
type $Props = $Translate & {
  item: $FullChampionship;
};

type $State = {
  data: Array<$FullChampionshipResultTable>;
  loading: boolean;
};

class ResultsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getChampionshipResultsSectionData({
        ChampionshipId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        const models = ['ChampionshipResultTable', 'ChampionshipResultTableEntry', 'ChampionshipResultTableRace'];

        if (data.ChampionshipId === item.id && _.includes(
          models,
          data.modelName,
        )) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  renderHead(dataItem: $FullChampionshipResultTable) {
    const {
      t,
    } = this.props;

    return (
      <thead>
        <tr>
          <th className={'text-left text-green-600 uppercase w-48'}>
            {dataItem.Name}
          </th>
          {dataItem.ChampionshipResultTableRaces.map((
            race: $FullChampionshipResultTableRace,
          ) => (
            <th
              key={race.id}
              title={race.Name}
            >
              {race.Slug}
            </th>
          ))}
          <th
            className={'uppercase w-48'}
            colSpan={2}
          >
            {t('Places')}
          </th>
        </tr>
        <tr className={'border-top text-gray-600 uppercase'}>
          <td className={'text-left py-1-i pl-2'}>
            {t('Entries')}
          </td>
          {dataItem.ChampionshipResultTableRaces.map((
            race: $FullChampionshipResultTableRace,
          ) => (
            <td
              className={'text-center py-1-i'}
              key={race.id}
            >
              {t('Pts.')}
            </td>
          ))}
          <td className={'text-center py-1-i w-24'}>
            {t('Pts.')}
          </td>
          <td className={'text-center py-1-i w-24'}>
            {t('Plc.')}
          </td>
        </tr>
      </thead>
    );
  }

  renderBody(dataItem: $FullChampionshipResultTable) {
    const {
      t,
    } = this.props;

    const sortedChpResultTableEntries: Array<$FullChampionshipResultTableEntry> = sortResults(
      dataItem.ChampionshipResultTableEntries,
      dataItem.InvertPoints,
    );

    return (
      <tbody>
        {sortedChpResultTableEntries.map((
          championshipResultTableEntry: $FullChampionshipResultTableEntry,
        ) => (
          <tr key={championshipResultTableEntry.id}>
            <td title={t('Entry')}>
              <div className={'md:text-lg'}>
                {getChampionshipEntryTitle({
                  championshipResultTableEntry,
                })}
              </div>
              <div className={'text-xs md:text-sm text-gray-600'}>
                {getChampionshipEntrySubTitle({
                  championshipResultTableEntry,
                })}
              </div>
            </td>
            {dataItem.ChampionshipResultTableRaces.map((
              race: $FullChampionshipResultTableRace,
            ) => {
              const result: $Model<'ChampionshipResultTableRaceResult'> | undefined = _.find(
                race.ChampionshipResultTableRaceResults,
                {
                  ChampionshipResultTableEntryId: championshipResultTableEntry.id,
                },
              );

              return (
                <td
                  className={`text-center ${_.get(
                    result,
                    'Discarded',
                  ) === true ? 'bg-discarded text-gray-400' : ''}`}
                  key={race.id}
                >
                  {_.get(
                    result,
                    'Points',
                    '-',
                  )}
                </td>
              );
            })}
            <td className={'text-center font-bold bg-blue-50'}>
              {championshipResultTableEntry.Points}
            </td>
            <td className={'text-center text-lg font-bold bg-blue text-white'}>
              {championshipResultTableEntry.Place}
            </td>
          </tr>
        ))}
      </tbody>
    );
  }

  renderItem(dataItem: $FullChampionshipResultTable) {
    return (
      <table className={'table table_hoverable w-full mb-3'}>
        {this.renderHead(dataItem)}
        {this.renderBody(dataItem)}
      </table>
    );
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (
      <Section
        id={webSections[webPages.championshipPage].results}
        title={t('Results')}
      >
        <Loader
          fade
          loading={loading === true}
        >
          {data.length > 0 ? (
            <>
              {data.map((dataItem: $FullChampionshipResultTable) => (
                <div
                  className={'overflow-x-auto'}
                  key={dataItem.id}
                >
                  {this.renderItem(dataItem)}
                </div>
              ))}
            </>
          ) : (
            <NoData label={t('No published results yet')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default ResultsSectionView;
